import './DateFilter.scss';
import { ButtonGroup, Button, ToolbarSeparator } from '@progress/kendo-react-buttons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { useCallback, useState } from 'react';
import { DateFilterTab, VisualData } from '../../models/date-filter.model';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  selectTempVisualType,
  selectTempVisualData,
  selectTempStartDate,
  selectTempEndDate,
  setVisualType,
  setVisualData,
  setStartDate,
  setEndDate,
  selectTempDateFilter,
} from '../../store/slices/date-filter.slice';
import { DATE_DROPDOWN_UNITS } from '../../constants';
import { RootState } from '../../../common/store';
import { DatasetID } from '../../../common/models/filter.model';
import FlyoutDropDownItem from '../flyouts/FlyoutDropDownItem';
import { caulculateDatesFromLastPeriod } from 'common/hooks/utils';
import { Switch, SwitchChangeEvent } from '@progress/kendo-react-inputs';
import {
  selectOverrideDateFilter,
  updateOverrideDateFilter,
} from 'common/store/slices/dashboard-editor.slice';
import { KWELLO_ICON, KwelloSvgIcon } from '../icon/KwelloSvgIcon';
import { Tooltip } from 'react-tooltip';
import KwelloDatePicker from './KwelloDatePicker';

export const DROPDOWN_VALUES: { [key: string]: number[] } = {
  [DATE_DROPDOWN_UNITS.Days]: Array(365)
    .fill('')
    .map((_, i) => i + 1),
  [DATE_DROPDOWN_UNITS.Weeks]: Array(52)
    .fill('')
    .map((_, i) => i + 1),
  [DATE_DROPDOWN_UNITS.Months]: Array(12)
    .fill('')
    .map((_, i) => i + 1),
  [DATE_DROPDOWN_UNITS.Years]: Array(10)
    .fill('')
    .map((_, i) => i + 1),
};

export const DROPDOWN_UNITS = [
  DATE_DROPDOWN_UNITS.Days,
  DATE_DROPDOWN_UNITS.Weeks,
  DATE_DROPDOWN_UNITS.Months,
  DATE_DROPDOWN_UNITS.Years,
];

const filterOverrideTooltip =
  'When enabled, this will apply the selected date range to all tiles on the dashboard, overriding individual tile date filters. Toggle off to revert to the original date ranges set by each tile.';

enum DropdownOpen {
  NONE = -1,
  COUNT = 0,
  UNIT = 1,
}

export function DateFilter({ datasetId }: Readonly<DatasetID>) {
  const dispatch = useAppDispatch();
  const [dropdownIdOpen, setDropdownIdOpen] = useState<DropdownOpen>(DropdownOpen.NONE);
  const isUniversalFilters = datasetId.includes('universal-');
  const actualDateFilter = useAppSelector((state: RootState) =>
    selectTempDateFilter(state, datasetId)
  );
  const defaultActiveTab = useAppSelector((state: RootState) =>
    selectTempVisualType(state, datasetId)
  );
  const defaultVisualRepresentation = useAppSelector((state: RootState) =>
    selectTempVisualData(state, datasetId)
  );
  const defaultStartDate = useAppSelector((state: RootState) =>
    selectTempStartDate(state, datasetId)
  );
  const defaultEndDate = useAppSelector((state: RootState) => selectTempEndDate(state, datasetId));
  const overrideDateFilter = useAppSelector(selectOverrideDateFilter);
  const startDate = isUniversalFilters ? actualDateFilter.startDate : defaultStartDate;
  const endDate = isUniversalFilters ? actualDateFilter.endDate : defaultEndDate;
  const activeTab = isUniversalFilters ? actualDateFilter.visualType : defaultActiveTab;
  const visualRepresentation = isUniversalFilters
    ? actualDateFilter.visualData
    : defaultVisualRepresentation;

  const setActiveTab = useCallback(
    (nextValue: DateFilterTab) => {
      dispatch(setVisualType({ datasetId, visualType: nextValue }));
    },
    [datasetId, dispatch]
  );

  const isTabActive = useCallback(
    (value: DateFilterTab) => {
      return value === activeTab;
    },
    [activeTab]
  );

  const setVisualRepresentationData = useCallback(
    (index: number, value: string | number) => {
      const next: VisualData = [...visualRepresentation];

      next[index] = value;

      if (index === 1) {
        const exists = DROPDOWN_VALUES[value].filter((v) => v == next[0]);
        next[0] = exists.length > 0 ? next[0] : DROPDOWN_VALUES[value].at(0) ?? 1;
      }

      const calculatedStartDate = caulculateDatesFromLastPeriod(next);

      dispatch(setVisualData({ datasetId, visualData: next }));
      dispatch(setStartDate({ datasetId, date: new Date(calculatedStartDate?.start as string) }));
    },
    [visualRepresentation, datasetId, dispatch]
  );

  const toggleOverrideDateFilter = (event: SwitchChangeEvent) => {
    dispatch(updateOverrideDateFilter(event.value));
  };

  const openDateFilterDropdown = (id: DropdownOpen) => {
    setDropdownIdOpen((prev) => (prev === id ? -1 : id));
  };

  const isDropdownOpen = (id: DropdownOpen) => {
    return dropdownIdOpen === id;
  };

  return (
    <div className="date-filter-container">
      <div className="date-filter-title-container">
        <label className="k-label k-label-lg form-label-row">Date Range</label>
        {isUniversalFilters && (
          <div
            className={`date-filter-override-switch-container ${overrideDateFilter ? 'active' : ''}`}
          >
            <Tooltip
              id="date-filter-switch-tooltip"
              style={{ fontSize: '0.8rem', maxWidth: 300, zIndex: 9999 }}
            >
              {filterOverrideTooltip}
            </Tooltip>
            <span>Override Date Range for Dashboard</span>
            <span data-tooltip-id="date-filter-switch-tooltip">
              <KwelloSvgIcon icon={KWELLO_ICON.QUESTIONMARK_ICON} />
            </span>
            <Switch
              className="date-filter-override-switch"
              offLabel=""
              onLabel=""
              checked={overrideDateFilter}
              onChange={toggleOverrideDateFilter}
              size="small"
            />
          </div>
        )}
      </div>
      <div className="date-filter-tabs">
        <ButtonGroup className="button-group-tabs">
          <Button
            onClick={() => setActiveTab(DateFilterTab.LAST)}
            selected={isTabActive(DateFilterTab.LAST)}
            togglable={true}
            fillMode="flat"
          >
            Last
          </Button>
          <Button
            onClick={() => setActiveTab(DateFilterTab.SINCE)}
            selected={isTabActive(DateFilterTab.SINCE)}
            togglable={true}
            fillMode="flat"
          >
            Since
          </Button>
          <Button
            onClick={() => setActiveTab(DateFilterTab.BETWEEN)}
            selected={isTabActive(DateFilterTab.BETWEEN)}
            togglable={true}
            fillMode="flat"
          >
            Between
          </Button>
        </ButtonGroup>
      </div>
      <div className="date-filter-content">
        {isTabActive(DateFilterTab.LAST) && (
          <div className="date-filter-content-tab-last">
            <div className="cell">
              <label className="k-label k-label-lg">Last</label>
            </div>
            <div className="cell-dropdown-values">
              <DropDownList
                value={visualRepresentation[0]}
                data={DROPDOWN_VALUES[visualRepresentation[1]]}
                onChange={(e) => setVisualRepresentationData(0, e.value)}
                size={'small'}
                itemRender={FlyoutDropDownItem}
                fillMode={'outline'}
                opened={isDropdownOpen(DropdownOpen.COUNT)}
                onOpen={() => openDateFilterDropdown(DropdownOpen.COUNT)}
                onClose={() => openDateFilterDropdown(DropdownOpen.NONE)}
              ></DropDownList>
            </div>
            <div className="cell-dropdown-units">
              <DropDownList
                value={visualRepresentation[1]}
                data={DROPDOWN_UNITS}
                onChange={(e) => setVisualRepresentationData(1, e.value)}
                size={'small'}
                itemRender={FlyoutDropDownItem}
                fillMode={'outline'}
                opened={isDropdownOpen(DropdownOpen.UNIT)}
                onOpen={() => openDateFilterDropdown(DropdownOpen.UNIT)}
                onClose={() => openDateFilterDropdown(DropdownOpen.NONE)}
              ></DropDownList>
            </div>
          </div>
        )}
        {isTabActive(DateFilterTab.SINCE) && (
          <div className="date-filter-content-tab-since">
            <div className="form-row">
              <KwelloDatePicker
                defaultValue={new Date(startDate)}
                max={new Date()}
                onChange={(e) => dispatch(setStartDate({ datasetId, date: e.value as Date }))}
                size={'small'}
                tabIndex={0}
              />
            </div>
          </div>
        )}
        {isTabActive(DateFilterTab.BETWEEN) && (
          <div className="date-filter-content-tab-between">
            <div className="calendar-from-to">
              <div className="form-row">
                <div className="cell-label">
                  <label className="k-label label-up">From</label>
                </div>
                <div className="cell cell-datepicker">
                  <KwelloDatePicker
                    defaultValue={new Date(startDate)}
                    max={new Date(endDate)}
                    onChange={(e) => dispatch(setStartDate({ datasetId, date: e.value as Date }))}
                    size={'small'}
                    width={150}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="cell-label">
                  <label className="k-label label-up">To</label>
                </div>{' '}
                <div className="cell-datepicker">
                  <KwelloDatePicker
                    defaultValue={new Date(endDate)}
                    min={new Date(startDate)}
                    onChange={(e) => dispatch(setEndDate({ datasetId, date: e.value as Date }))}
                    size={'small'}
                    width={150}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ToolbarSeparator />
    </div>
  );
}
